/**
 * MapPage - Page avec carte Leaflet interactive
 * @class MapPage
 */
import { getIcon } from '../utils/icons.js';
import { t, getCurrentLang } from '../utils/i18n.js';

// URL du GeoJSON des frontières des pays
const GEOJSON_URL = 'https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson';

export class MapPage {
    /**
     * Crée une instance de MapPage
     * @param {Object} options - Options de la page
     */
    constructor(options = {}) {
        this._services = options.services || {};
        this._state = options.state || {};
        this._onCountrySelect = options.onCountrySelect;
        
        this._map = null;
        this._geoJsonLayer = null;
        this._countryLayers = new Map(); // Map code -> layer
        this._selectedCountry = null;
        this._selectedLayer = null;
        this._container = null;
        this._L = null;
        this._geoJsonData = null;
    }

    /**
     * Retourne la liste des pays
     * @returns {Array}
     */
    get countries() {
        return this._state.countries || [];
    }

    /**
     * Rend la page
     * @param {HTMLElement} container - Conteneur
     */
    async render(container) {
        this._container = container;
        
        container.innerHTML = `
            <div class="page">
                <div class="page-header">
                    <h1 class="page-title">
                        <span class="page-title-icon">${getIcon('map')}</span>
                        ${t('map_title')}
                    </h1>
                    <p class="page-subtitle">${t('map_subtitle')}</p>
                </div>
                
                <div class="map-page">
                    <div class="map-container" id="leaflet-map"></div>
                    
                    <div class="country-panel">
                        <div class="card">
                            <div class="card-header">
                                <h3 class="card-title">
                                    <span class="card-title-icon">${getIcon('search')}</span>
                                    ${t('search_title')}
                                </h3>
                            </div>
                            <div class="card-body">
                                <div class="search-box">
                                    <span class="search-icon">${getIcon('search')}</span>
                                    <input type="text" class="search-input" id="country-search" placeholder="${t('search_placeholder')}">
                                </div>
                            </div>
                        </div>
                        
                        <div class="card country-card" id="country-panel">
                            <div class="card-body">
                                <div class="empty-state">
                                    <div class="empty-state-icon">${getIcon('mapPin')}</div>
                                    <h3 class="empty-state-title">${t('no_country_selected')}</h3>
                                    <p class="empty-state-description">${t('click_map_hint')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;

        await this._initializeMap();
        this._setupEventListeners();
    }

    /**
     * Initialise la carte Leaflet
     * @private
     */
    async _initializeMap() {
        try {
            // Import dynamique de Leaflet
            const L = await import('leaflet');
            this._L = L.default || L;
            
            // Import des styles Leaflet
            await import('leaflet/dist/leaflet.css');

            // Crée la carte
            this._map = this._L.map('leaflet-map', {
                center: [20, 0],
                zoom: 2,
                minZoom: 2,
                maxZoom: 18,
                worldCopyJump: true
            });

            // Ajoute la couche de tuiles (style carto light)
            this._L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
                subdomains: 'abcd',
                maxZoom: 20
            }).addTo(this._map);

            // Charge et ajoute les frontières des pays
            await this._loadCountryBorders();

            // Ajoute une couche de labels par-dessus
            this._L.tileLayer('https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png', {
                attribution: '',
                subdomains: 'abcd',
                maxZoom: 20,
                pane: 'shadowPane' // Met les labels au-dessus
            }).addTo(this._map);

            // Essaie de centrer sur la position de l'utilisateur
            this._centerOnUserLocation();

        } catch (error) {
            console.error('[MapPage] Erreur lors de l\'initialisation de la carte:', error);
            const mapContainer = document.getElementById('leaflet-map');
            if (mapContainer) {
                mapContainer.innerHTML = `
                    <div class="empty-state">
                        <div class="empty-state-icon">${getIcon('alertTriangle')}</div>
                        <h3 class="empty-state-title">${t('error_title')}</h3>
                        <p class="empty-state-description">${t('map_error')}</p>
                    </div>
                `;
            }
        }
    }

    /**
     * Charge les frontières des pays en GeoJSON
     * @private
     */
    async _loadCountryBorders() {
        try {
            // Charge le GeoJSON des frontières
            const response = await fetch(GEOJSON_URL);
            if (!response.ok) throw new Error('Erreur de chargement du GeoJSON');
            
            this._geoJsonData = await response.json();
            
            // Style par défaut des pays
            const defaultStyle = {
                fillColor: '#4f46e5',
                fillOpacity: 0.1,
                color: '#4f46e5',
                weight: 1,
                opacity: 0.5
            };

            // Style au survol
            const hoverStyle = {
                fillColor: '#4f46e5',
                fillOpacity: 0.4,
                color: '#4f46e5',
                weight: 2,
                opacity: 1
            };

            // Style sélectionné
            const selectedStyle = {
                fillColor: '#10b981',
                fillOpacity: 0.5,
                color: '#10b981',
                weight: 3,
                opacity: 1
            };

            // Créer une map pour lookup rapide des traductions
            const countriesMap = new Map();
            const lang = getCurrentLang();
            
            if (this.countries) {
                this.countries.forEach(c => {
                    if (c.code) countriesMap.set(c.code, c);
                });
            }

            // Crée la couche GeoJSON
            this._geoJsonLayer = this._L.geoJSON(this._geoJsonData, {
                style: defaultStyle,
                onEachFeature: (feature, layer) => {
                    const countryCode = feature.properties.ISO_A2 || feature.properties.ISO_A3?.substring(0, 2);
                    let countryName = feature.properties.ADMIN || feature.properties.name;
                    
                    // Tenter de trouver le nom traduit
                    const matchedCountry = countriesMap.get(countryCode);
                    if (matchedCountry) {
                        countryName = matchedCountry.getName(lang);
                    }

                    const iso3 = feature.properties.ISO_A3;
                    
                    // Stocke les données directement sur le layer
                    layer._countryData = {
                        code: countryCode,
                        name: countryName,
                        originalName: feature.properties.ADMIN || feature.properties.name,
                        iso3: iso3,
                        properties: feature.properties
                    };
                    
                    // Stocke la référence layer -> pays
                    if (countryCode) {
                        this._countryLayers.set(countryCode, layer);
                    }

                    // Événements de survol
                    layer.on('mouseover', (e) => {
                        if (this._selectedLayer !== layer) {
                            layer.setStyle(hoverStyle);
                            layer.bringToFront();
                        }
                        
                        // Tooltip avec le nom du pays
                        layer.bindTooltip(countryName, {
                            permanent: false,
                            direction: 'top',
                            className: 'country-tooltip'
                        }).openTooltip();
                    });

                    layer.on('mouseout', (e) => {
                        if (this._selectedLayer !== layer) {
                            layer.setStyle(defaultStyle);
                        }
                        layer.closeTooltip();
                    });

                    // Événement de clic - utilise les données stockées sur le layer
                    layer.on('click', (e) => {
                        this._L.DomEvent.stopPropagation(e);
                        const clickedLayer = e.target;
                        const data = clickedLayer._countryData;
                        console.log('[MapPage] Click - données:', data?.name, data?.code);
                        if (data) {
                            this._handleCountryClick(clickedLayer, data);
                        }
                    });
                }
            }).addTo(this._map);

            // Stocke les styles pour les réutiliser
            this._styles = { default: defaultStyle, hover: hoverStyle, selected: selectedStyle };

        } catch (error) {
            console.error('[MapPage] Erreur lors du chargement des frontières:', error);
        }
    }

    /**
     * Gère le clic sur un pays
     * @private
     */
    _handleCountryClick(layer, data) {
        console.log('[MapPage] Pays cliqué:', data.name, data.code, data.iso3);

        // Réinitialise le style du pays précédemment sélectionné
        if (this._selectedLayer && this._selectedLayer !== layer) {
            this._selectedLayer.setStyle(this._styles.default);
        }

        // Applique le style sélectionné
        layer.setStyle(this._styles.selected);
        layer.bringToFront();
        this._selectedLayer = layer;

        // Cherche dans les données RestCountries avec plusieurs stratégies
        const country = this._findCountryMatch(data);

        console.log('[MapPage] Pays trouvé dans RestCountries:', country?.name?.common || country?.name || 'Non trouvé');

        if (country) {
            this._selectedCountry = country;
            this._updateCountryPanel(country);
            
            // Zoom sur le pays
            this._map.fitBounds(layer.getBounds(), { 
                padding: [50, 50],
                maxZoom: 6 
            });

            // Callback externe
            if (this._onCountrySelect) {
                this._onCountrySelect(country);
            }
        } else {
            // Si pas trouvé dans RestCountries, affiche les infos de base
            this._updateCountryPanelBasic(data.properties);
        }
    }

    /**
     * Trouve le pays correspondant dans RestCountries
     * @private
     */
    _findCountryMatch(data) {
        const searchName = (data.originalName || data.name || '').toLowerCase();
        
        return this.countries.find(c => {
            const cCode = c.cca2 || c.code;
            const cca3 = c.cca3;
            // Gère le cas où name est un objet ou une chaîne
            const cNameCommon = c.name?.common || (typeof c.name === 'string' ? c.name : '');
            const cNameOfficial = c.name?.official || (typeof c.name === 'string' ? c.name : '');
            
            const commonName = cNameCommon.toLowerCase();
            const officialName = cNameOfficial.toLowerCase();
            
            const nativeName = c.name?.nativeName ? Object.values(c.name.nativeName).map(n => n.common?.toLowerCase()) : [];
            const altSpellings = (c.altSpellings || []).map(s => s.toLowerCase());
            
            // Correspondance par code ISO
            if (data.code && cCode === data.code) return true;
            if (data.iso3 && cca3 === data.iso3) return true;
            
            // Correspondance exacte par nom
            if (searchName && commonName === searchName) return true;
            if (searchName && officialName === searchName) return true;
            
            // Correspondance partielle par nom (contient) - seulement si les noms ne sont pas vides
            if (searchName && searchName.length > 3) {
                if (commonName && (commonName.includes(searchName) || searchName.includes(commonName))) return true;
                if (officialName && (officialName.includes(searchName) || searchName.includes(officialName))) return true;
            }
            
            // Correspondance par altSpellings
            if (searchName && altSpellings.some(alt => alt === searchName || alt.includes(searchName) || searchName.includes(alt))) return true;
            
            // Correspondance par nom natif
            if (searchName && nativeName.some(n => n === searchName)) return true;
            
            return false;
        });
    }

    /**
     * Affiche les infos de base d'un pays (si pas dans RestCountries)
     * @private
     */
    _updateCountryPanelBasic(properties) {
        const panel = this._container?.querySelector('#country-panel');
        if (!panel) return;

        const name = properties?.ADMIN || properties?.name || 'Inconnu';

        panel.innerHTML = `
            <div class="card-body">
                <div class="country-header">
                    <div>
                        <h3 class="country-name">${name}</h3>
                        <p class="country-region">${properties?.CONTINENT || ''}</p>
                    </div>
                </div>
                <p class="text-muted mt-md">${t('no_detailed_info')}</p>
            </div>
        `;
    }

    /**
     * Centre sur la position de l'utilisateur
     * @private
     */
    async _centerOnUserLocation() {
        if (this._state.userLocation) {
            const { latitude, longitude, countryCode } = this._state.userLocation;
            if (latitude && longitude) {
                this._map.setView([latitude, longitude], 5);
                
                // Sélectionne le pays si trouvé
                if (countryCode) {
                    const country = this.countries.find(c => 
                        (c.cca2 || c.code) === countryCode
                    );
                    if (country) {
                        setTimeout(() => this._selectCountry(country), 500);
                    }
                }
            }
        }
    }

    /**
     * Configure les écouteurs d'événements
     * @private
     */
    _setupEventListeners() {
        // Recherche de pays
        const searchInput = this._container?.querySelector('#country-search');
        if (searchInput) {
            searchInput.addEventListener('input', (e) => {
                this._searchCountries(e.target.value);
            });
        }

        // Délégation d'événement pour les boutons dans les popups
        document.addEventListener('click', (e) => {
            if (e.target.classList.contains('map-popup-btn')) {
                const code = e.target.dataset.countryCode;
                if (code) {
                    const country = this.countries.find(c => (c.cca2 || c.code) === code);
                    if (country) {
                        this._selectCountry(country);
                    }
                }
            }
        });
    }

    /**
     * Recherche des pays
     * @param {string} query - Terme de recherche
     * @private
     */
    _searchCountries(query) {
        const normalizedQuery = query.toLowerCase().trim();
        
        if (!normalizedQuery || !this._styles) {
            // Réinitialise tous les styles
            this._countryLayers.forEach((layer) => {
                if (layer !== this._selectedLayer) {
                    layer.setStyle(this._styles?.default || {
                        fillColor: '#4f46e5',
                        fillOpacity: 0.1,
                        color: '#4f46e5',
                        weight: 1,
                        opacity: 0.5
                    });
                }
            });
            return;
        }

        // Filtre les pays - réduit l'opacité des non-matchés
        let matchCount = 0;
        let lastMatch = null;

        const lang = getCurrentLang();

        this.countries.forEach(country => {
            const name = (country.name?.common || country.name || '').toLowerCase();
            const translatedName = country.getName(lang).toLowerCase();
            const code = (country.cca2 || country.code || '').toLowerCase();
            const matches = name.includes(normalizedQuery) || translatedName.includes(normalizedQuery) || code.includes(normalizedQuery);
            
            const layer = this._countryLayers.get(country.cca2 || country.code);
            if (layer && layer !== this._selectedLayer) {
                layer.setStyle({
                    ...this._styles.default,
                    fillOpacity: matches ? 0.3 : 0.02,
                    opacity: matches ? 0.8 : 0.2
                });
            }

            if (matches) {
                matchCount++;
                lastMatch = country;
            }
        });

        // Si un seul résultat, sélectionne le pays
        if (matchCount === 1 && lastMatch) {
            this._selectCountry(lastMatch);
        }
    }

    /**
     * Sélectionne un pays (via recherche ou API)
     * @param {Object} country - Pays
     * @private
     */
    _selectCountry(country) {
        const code = country.cca2 || country.code;

        // Réinitialise le style du pays précédemment sélectionné
        if (this._selectedLayer && this._styles) {
            this._selectedLayer.setStyle(this._styles.default);
        }

        this._selectedCountry = country;

        // Trouve et sélectionne le layer correspondant
        const layer = this._countryLayers.get(code);
        if (layer && this._styles) {
            layer.setStyle(this._styles.selected);
            layer.bringToFront();
            this._selectedLayer = layer;

            // Zoom sur le pays
            this._map.fitBounds(layer.getBounds(), {
                padding: [50, 50],
                maxZoom: 6,
                animate: true
            });
        } else {
            // Fallback: centre sur les coordonnées
            let lat, lng;
            if (country.latlng && country.latlng.length >= 2) {
                [lat, lng] = country.latlng;
            } else {
                lat = country.latitude;
                lng = country.longitude;
            }

            if (this._map && lat && lng) {
                this._map.setView([lat, lng], 5, { animate: true });
            }
        }

        // Met à jour le panneau d'information
        this._updateCountryPanel(country);

        // Callback externe
        if (this._onCountrySelect) {
            this._onCountrySelect(country);
        }
    }

    /**
     * Met à jour le panneau d'information
     * @param {Object} country - Pays
     * @private
     */
    _updateCountryPanel(country) {
        const panel = this._container?.querySelector('#country-panel');
        if (!panel) return;

        const name = country.getName(getCurrentLang());
        const capital = country.capital ? (Array.isArray(country.capital) ? country.capital[0] : country.capital) : 'N/A';
        const region = country.region || 'N/A';
        const subregion = country.subregion || '';
        const population = country.population ? country.population.toLocaleString(getCurrentLang() === 'fr' ? 'fr-FR' : 'en-US') : 'N/A';
        const flag = country.flags?.svg || country.flags?.png || '';
        
        // Devise - extraction améliorée
        let currencyCode = '';
        let currencyName = '';
        let currencySymbol = '';
        
        if (country.currencies) {
            // Si c'est un tableau (ex: API v2 ou format différent), ou un objet (API v3)
            const currencies = country.currencies;
            
            if (Array.isArray(currencies) && currencies.length > 0) {
                // Cas d'un tableau: [{code: "USD", name: "Dollar", ...}]
                const curr = currencies[0];
                currencyCode = curr.code || '';
                currencyName = curr.name || '';
                currencySymbol = curr.symbol || '';
            } else if (typeof currencies === 'object') {
                // Cas d'un objet: { "USD": { name: "Dollar", ... } }
                const codes = Object.keys(currencies);
                if (codes.length > 0) {
                    // Vérifie si la clé est un index numérique (ce qui indiquerait un array converti en objet)
                    const firstKey = codes[0];
                    if (!isNaN(parseInt(firstKey)) && currencies[firstKey].code) {
                        // C'est un pseudo-array
                        const curr = currencies[firstKey];
                        currencyCode = curr.code || '';
                        currencyName = curr.name || '';
                        currencySymbol = curr.symbol || '';
                    } else {
                        // C'est le format standard v3 avec Code comme clé
                        currencyCode = firstKey;
                        const curr = currencies[currencyCode];
                        if (curr && typeof curr === 'object') {
                            currencyName = curr.name || '';
                            currencySymbol = curr.symbol || '';
                        }
                    }
                }
            }
        }
        
        // Debug devise
        console.log('[MapPage] Devise extraite:', { currencyCode, currencyName, currencySymbol });

        // Langues
        let languages = '';
        if (country.languages && typeof country.languages === 'object') {
            languages = Object.values(country.languages).slice(0, 3).join(', ');
        }

        // Coordonnées
        let lat = 0, lng = 0;
        if (country.latlng && country.latlng.length >= 2) {
            [lat, lng] = country.latlng;
        }

        // Format de la devise plus lisible
        let currencyDisplay = '';
        if (currencyCode && currencyCode !== '0' && typeof currencyCode === 'string') {
            currencyDisplay = currencyName || currencyCode;
            if (currencySymbol && currencySymbol !== currencyCode) {
                currencyDisplay += ` (${currencySymbol})`;
            }
            currencyDisplay += ` - ${currencyCode}`;
        }

        panel.innerHTML = `
            <div class="card-body">
                <div class="country-header">
                    ${flag ? `<img src="${flag}" alt="${name}" class="country-flag">` : ''}
                    <div>
                        <h3 class="country-name">${name}</h3>
                        <p class="country-region">${region}${subregion ? ` - ${subregion}` : ''}</p>
                    </div>
                </div>
                
                <div class="country-details">
                    <div class="country-detail-item">
                        <span class="country-detail-icon">${getIcon('building')}</span>
                        <div>
                            <span class="country-detail-label">${t('capital')}</span>
                            <span class="country-detail-value">${capital}</span>
                        </div>
                    </div>
                    
                    <div class="country-detail-item">
                        <span class="country-detail-icon">${getIcon('users')}</span>
                        <div>
                            <span class="country-detail-label">${t('population')}</span>
                            <span class="country-detail-value">${population}</span>
                        </div>
                    </div>
                    
                    ${currencyCode ? `
                    <div class="country-detail-item">
                        <span class="country-detail-icon">${getIcon('dollarSign')}</span>
                        <div>
                            <span class="country-detail-label">${t('currency')}</span>
                            <span class="country-detail-value">${currencyDisplay}</span>
                        </div>
                    </div>
                    ` : ''}

                    ${languages ? `
                    <div class="country-detail-item">
                        <span class="country-detail-icon">${getIcon('globe')}</span>
                        <div>
                            <span class="country-detail-label">${t('languages')}</span>
                            <span class="country-detail-value">${languages}</span>
                        </div>
                    </div>
                    ` : ''}
                    
                    <div class="country-detail-item">
                        <span class="country-detail-icon">${getIcon('mapPin')}</span>
                        <div>
                            <span class="country-detail-label">${t('coordinates')}</span>
                            <span class="country-detail-value">${lat.toFixed(2)}°, ${lng.toFixed(2)}°</span>
                        </div>
                    </div>
                </div>
            </div>
            
            ${currencyCode ? `
            <div class="card-footer">
                <a href="#/converter" class="btn btn-primary" style="width: 100%; justify-content: center;">
                    ${getIcon('exchange')}
                    ${t('convert_btn')} ${currencyCode}
                </a>
            </div>
            ` : ''}
        `;
    }

    /**
     * Détruit la page
     */
    destroy() {
        if (this._map) {
            if (this._geoJsonLayer) {
                this._map.removeLayer(this._geoJsonLayer);
            }
            this._map.remove();
            this._map = null;
        }
        this._countryLayers.clear();
        this._geoJsonLayer = null;
        this._selectedLayer = null;
        this._geoJsonData = null;
    }
}
